import React, { useState, useEffect } from 'react'
import { Plus, Menu, Shield, CheckCircle } from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import Breadcrumb from '../../components/Common/Breadcrumb'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import menuItemService from '../../services/menuItemService'

const MenuItemMaster = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [showHelp, setShowHelp] = useState(false)

  const loadData = async () => {
    try {
      setLoading(true)
      const [itemsRes, statsRes] = await Promise.all([
        menuItemService.getAllMenuItems({ includeInactive: true }),
        menuItemService.getMenuItemStats().catch(() => ({ success: false }))
      ])
      if (itemsRes?.success && Array.isArray(itemsRes.data)) {
        setData(itemsRes.data.map(item => ({ ...item, id: item._id })))
      }
      if (statsRes?.success && statsRes.data) {
        setStats({
          total: statsRes.data.total ?? 0,
          active: statsRes.data.active ?? 0,
          inactive: statsRes.data.inactive ?? 0
        })
      }
    } catch (err) {
      console.error(err)
      toast.error('Failed to load menu items')
      setData([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const columns = [
    { accessorKey: 'menuCode', header: 'Menu Code', cell: ({ getValue }) => <span className="code-text">{getValue()}</span> },
    { accessorKey: 'label', header: 'Label', cell: ({ getValue }) => <span className="name-text">{getValue()}</span> },
    { accessorKey: 'path', header: 'Path', cell: ({ getValue }) => <span className="description-text">{getValue() || '-'}</span> },
    { accessorKey: 'icon', header: 'Icon', cell: ({ getValue }) => <span>{getValue() || '-'}</span> },
    { accessorKey: 'parent', header: 'Parent', cell: ({ row }) => (row.original.parent?.label || '-') },
    { accessorKey: 'sortOrder', header: 'Sort', cell: ({ getValue }) => getValue() ?? 0 },
    { accessorKey: 'isActive', header: 'Status', cell: ({ getValue }) => <span className={`status-badge ${getValue() ? 'active' : 'inactive'}`}>{getValue() ? 'Active' : 'Inactive'}</span> }
  ]

  const handleAddNew = () => navigate('/settings/menu-item-master/form')
  const handleView = (row) => navigate('/settings/menu-item-master/view', { state: { menuItemData: row } })
  const handleEdit = (row) => navigate('/settings/menu-item-master/edit', { state: { menuItemData: row } })
  const handleDelete = (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem?.id) return
    try {
      const res = await menuItemService.deleteMenuItem(deleteItem.id)
      if (res?.success) {
        toast.success('Menu item deleted')
        setShowDeleteModal(false)
        setDeleteItem(null)
        loadData()
      } else {
        toast.error(res?.message || 'Failed to delete')
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to delete')
    }
  }

  const helpContent = (
    <>
      <h3>What is Menu Item Master?</h3>
      <p>Manage <span className="help-term">menu configuration and items</span> used in the application sidebar and navigation. Each item has a code, label, path, optional parent, and sort order.</p>
      <h3>Navigation</h3>
      <p>Use the breadcrumb to go back to Settings or Security Master. Use <strong>View</strong>, <strong>Edit</strong>, or <strong>Delete</strong> from the action menu on each row.</p>
      <h3>Columns</h3>
      <p><strong>Menu Code</strong> – Unique code. <strong>Label</strong> – Display text. <strong>Path</strong> – Route (e.g. /settings). <strong>Icon</strong> – Icon name. <strong>Parent</strong> – Parent menu if any. <strong>Sort</strong> – Display order. <strong>Status</strong> – Active or Inactive.</p>
    </>
  )

  const breadcrumbItems = [
    { label: 'Settings', path: '/settings', onClick: () => navigate('/settings') },
    { label: 'Masters', path: '/settings', onClick: () => navigate('/settings') },
    { label: 'Menu Item Master', path: '/settings/menu-item-master', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <Menu size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Menu Item Master</h1>
          <p>Menu configuration and items</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleAddNew}>
            <Plus size={18} />
            Add New
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon"><Shield size={16} /></div>
            <div className="metric-trend positive"><span>{stats.total}</span></div>
          </div>
          <div className="metric-value">{stats.total}</div>
          <div className="metric-title">Total Items</div>
          <div className="metric-subtitle">All menu items</div>
        </div>
        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon"><CheckCircle size={16} /></div>
            <div className="metric-trend positive"><span>{stats.active}</span></div>
          </div>
          <div className="metric-value">{stats.active}</div>
          <div className="metric-title">Active</div>
          <div className="metric-subtitle">Visible in menu</div>
        </div>
        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon"><Menu size={16} /></div>
            <div className="metric-trend neutral"><span>{stats.inactive}</span></div>
          </div>
          <div className="metric-value">{stats.inactive}</div>
          <div className="metric-title">Inactive</div>
          <div className="metric-subtitle">Hidden from menu</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          noCard
          pageSize={10}
          className="sac-master-table"
        />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteItem(null) }}
        onConfirm={confirmDelete}
        title="Delete Menu Item"
        message={deleteItem ? `Delete "${deleteItem.label}"? This cannot be undone.` : ''}
        confirmText="Delete"
        type="danger"
      />

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Menu Item Master – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default MenuItemMaster
